// src/screens/filmRoomV2/filmRoomModel.js
//
// Film Room v2 (A2) — THE SCREEN'S READING OF ONE TAPE DAY. Pure: no React, no
// Firestore, no clock (an instant is handed in where one is needed). Every
// value the screen renders is read here from the tape document by its PATH,
// and every number's class is the tape's own declaration for that path
// (`numberClasses`, BA-21 / BA-42 / F2) — never a per-widget constant. A label
// and its number therefore come from one source (BUILD_RULES §9).
//
// Spec: docs/specs/FILM_ROOM_BUILD_A_TAPE_SPEC_V1_2_20260927.md §7, §8 and
// Amendment E (docs/specs/FILM_ROOM_BUILD_A_TAPE_SPEC_V1_2_AMENDMENT_E_20261008.md).

import { classOfNumber, PROVENANCE_CLASSES, NON_CHECK_STATES } from '../../constants/filmTape';
import { getSessionForDate } from '../../utils/marketCalendar';
import { etDateOf } from '../../utils/tapeSchedule';

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const str = (v) => (typeof v === 'string' && v ? v : null);
const toMs = (v) => { if (typeof v !== 'string') return null; const ms = Date.parse(v); return Number.isFinite(ms) ? ms : null; };

// ── paths and classes ───────────────────────────────────────────────────────

/** The value at a concrete path (strings for keys, integers for indices), or undefined. */
export function valueAt(doc, path) {
  let node = doc;
  for (const seg of path) {
    if (node === null || node === undefined) return undefined;
    node = node[seg];
  }
  return node;
}

/**
 * The class the document declares for the number at `path` — one of the four,
 * or null when the declaration names none (or two). A null class is a defect
 * the screen shows as such; it never substitutes a class of its own.
 */
export function classAt(doc, path) {
  return classOfNumber(doc?.numberClasses, path);
}

/** A number the screen may render: a finite value at the path, with its declared class. */
export function numberAt(doc, path) {
  const value = valueAt(doc, path);
  if (!isNum(value)) return null;
  return { value, cls: classAt(doc, path), path };
}

export { PROVENANCE_CLASSES };

// ── the screen's own aggregates (counts of tape rows) ───────────────────────
//
// A count of the tape's own rows — how many checks a run of one state holds —
// is arithmetic on recorded values alone, so BA-21 classes it `derived`
// ("aftermath counts" are its own example). It is not a leaf of the document,
// so it has no path in `numberClasses`; this ONE declaration names each such
// count the screen renders, by what it counts, so no widget picks a class.
// Amendment E addendum R4(a): each number the screen computes is declared here
// with the class of its least-certain operand — the section counts and "n of
// m" are counts of recorded rows, so `derived`.
export const SCREEN_AGGREGATE_CLASSES = Object.freeze({
  'count(checks[] in a run)': 'derived',
  'count(slots of the derived held set)': 'derived',
  'count(actions[])': 'derived',
  'count(checks[] with a record)': 'derived',
  'count(tickSeqs in the minted range)': 'derived',
  'count(rationale[])': 'derived',
  'count(plans[] of the symbol)': 'derived',
  'ordinal(actions[] in time order)': 'derived',
  // Addendum 2, R8: a spelled-out quantity is a number. The subtitle's battle length ("one-day battle") is the
  // count of the battle's own timeline, timing.tradingDays — a count of recorded rows, so `derived`.
  'count(timing.tradingDays)': 'derived',
  // The Deep dive's facts and its percent axis, computed from a series document's bars: market operands only.
  'change(sessionOpen.value to bars[last].c)': 'market',
  'sum(bars[].v)': 'market',
  'axis(% from the session open)': 'market',
  // Film Room B2 (Amendment G RG-3, RG-8): a moment's number of checks is a count of stored tag items (derived);
  // the open-card count is a count of ledger entries (derived); the typed note's length is a count (derived);
  // the two founder slots S9 and S10 are copied from their versioned module, as the tape copies its slots (recorded).
  'count(tags.items[] in a moment)': 'derived',
  'count(plays[] open)': 'derived',
  'slot(S9 open cards per agent)': 'recorded',
  'count(characters in the note)': 'derived',
  'slot(S10 note characters)': 'recorded',
  // B2.1 A7 (Amendment J RJ-7): Glance's coverage line counts the symbols the candle pass did not chart — a count of
  // stored rows (coverage.series.notCharted), so derived.
  'count(coverage.series.notCharted[])': 'derived',
  // B2.1 B3 (Amendment H RH-6): the counter's count of the days in its window whose situations could not be read —
  // a count of tape days, derived
  'count(tape days with situations unavailable)': 'derived',
});

/**
 * "Checks · n of m" (R4(a)). `n` is the day's check rows that carry a record —
 * every row but a deferral and a missing record (NON_CHECK_STATES, the tape's
 * own "known checks"). `m` is the minted tickSeq range passes.close records:
 * its recorded range and the gaps it attributes to this day. `m` is null when
 * the close pass records no range, when it would be smaller than `n`, or when
 * any row with a record carries no tickSeq (an evaluation entry with no tick
 * record: its minted number may lie outside the range, so it cannot be placed
 * in `m` — review A2P1-1) — the screen then says "n recorded" and never states
 * a fraction the record does not support.
 */
export function checkCounts(tape) {
  const rows = Array.isArray(tape?.checks) ? tape.checks : [];
  const recorded = rows.filter((r) => !NON_CHECK_STATES.includes(r?.state));
  const n = recorded.length;
  const unplaced = recorded.some((r) => !Number.isInteger(r?.tickSeq));
  const close = tape?.passes?.close;
  const range = Array.isArray(close?.tickSeqRange) && close.tickSeqRange.length === 2 && close.tickSeqRange.every(Number.isInteger) ? close.tickSeqRange : [];
  const seqs = [...range, ...(Array.isArray(close?.gaps) ? close.gaps.filter(Number.isInteger) : [])];
  const m = seqs.length && !unplaced ? Math.max(...seqs) - Math.min(...seqs) + 1 : null;
  return { n, m: m !== null && m >= n ? m : null };
}

// ── Glance's one coverage line (B2.1 A7; Amendment J RJ-7) ──────────────────

/**
 * The sections Glance's one line sums up, in the screen's order: every coverage section the v2 screen renders —
 * `tags` aside (cards only, RG-2; the prepared case shows its own line) and `calls` aside (v2 renders no call).
 */
export const GLANCE_COVERAGE_SECTIONS = Object.freeze(['checks', 'actions', 'directives', 'meetings', 'plans', 'rationale', 'evidence', 'replay', 'series']);
const COVERAGE_STATUS_RANK = Object.freeze({ unavailable: 0, partial: 1, complete: 2 });

/**
 * The day's coverage as ONE line (RJ-7): each section that is not complete, by its stored status — named on the line
 * with its stored note — and the worst of them; none → "complete for every section". The series section counts the
 * symbols the candle pass did not chart (its W5 list): a series section stored complete with any is read as partial,
 * "n symbols not charted", n a derived count. A section the tape does not store (a tape written before that section
 * was taped) is named on the line as absent — "not on this tape" — and the line is then not complete (R14; review
 * SPR-S-2: skipping it said "complete for every section" of a tape that lacks one); an unknown status reads as
 * unavailable.
 */
export function coverageSummaryOf(tape) {
  const sections = [];
  for (const section of GLANCE_COVERAGE_SECTIONS) {
    const cov = tape?.coverage?.[section];
    if (!isObj(cov)) { sections.push({ section, status: 'absent', note: null, notCharted: 0 }); continue; }
    const stored = Object.prototype.hasOwnProperty.call(COVERAGE_STATUS_RANK, cov.status) ? cov.status : 'unavailable';
    const notCharted = section === 'series' && Array.isArray(cov.notCharted) ? cov.notCharted.length : 0;
    const status = stored === 'complete' && notCharted ? 'partial' : stored;
    if (status === 'complete') continue;
    sections.push({ section, status, note: stored !== 'complete' && typeof cov.note === 'string' && cov.note ? cov.note : null, notCharted });
  }
  // an absent section makes the line partial: the day is not covered in full, and nothing says it failed
  const rankOf = (s) => (s === 'absent' ? 'partial' : s);
  const status = sections.reduce((w, e) => (COVERAGE_STATUS_RANK[rankOf(e.status)] < COVERAGE_STATUS_RANK[w] ? rankOf(e.status) : w), 'complete');
  return { status, sections };
}

// ── time (ET wall clock; instants stay the tape's strings) ──────────────────

const ET_CLOCK = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true });
const ET_DAY = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const ET_DAY_SHORT = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });

/** "12:45 PM" for an instant, in ET; null for anything that is not an instant. */
export function etClock(instant) {
  const ms = toMs(instant);
  return ms === null ? null : ET_CLOCK.format(new Date(ms));
}

/**
 * An instant as a record's time on the tape day `etDate`: its ET clock, with its date when it fell on another
 * day ("Sep 22, 8:30 PM" — review A2L1-14, and the quotation's attribution, A2A1-3); null for no instant.
 */
export function etWhen(instant, etDate) {
  const clock = etClock(instant);
  if (clock === null) return null;
  const day = etDateOf(instant);
  return day && etDate && day !== etDate ? `${etDateLabel(day, { short: true })}, ${clock}` : clock;
}

/**
 * The trading session of an ET date, from the calendar of record the tape writers use
 * (src/utils/marketCalendar.js — api/_utils/filmTape/tapeTime.js sessionFor reads the same tables): its open and
 * its close, an early close where the calendar lists one. Null when the calendar does not know the date as a
 * session (a holiday, a weekend, a year outside its maintained horizon) — the screen then names no close.
 */
export function sessionOf(etDate) {
  const s = getSessionForDate(etDate);
  return s && s.isTradingDay && isNum(s.openMs) && isNum(s.closeMs) ? { openMs: s.openMs, closeMs: s.closeMs, early: s.isEarlyClose } : null;
}

/**
 * The battle's trading-day timeline, VALIDATED (Astra B4) — the one reading the day picker, the subtitle's battle
 * length and the harness's oracle share. It lives in src/utils/tapeSchedule.js since Film Room B1 W2, so the tape
 * writer reads the same timeline; re-exported here unchanged.
 */
export { tradingTimeline } from '../../utils/tapeSchedule';

/** "Tue, Sep 23, 2026" / "Sep 23" for an ET trading date `YYYY-MM-DD`. */
export function etDateLabel(etDate, { short = false } = {}) {
  if (typeof etDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(etDate)) return null;
  const ms = Date.parse(`${etDate}T12:00:00.000Z`);
  return (short ? ET_DAY_SHORT : ET_DAY).format(new Date(ms));
}

// ── number formats (display only — the value is the tape's) ─────────────────

const MINUS = '−';
const trim = (s) => (s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s);

/** Points: signed, up to two decimals, the true minus sign. */
export function fmtPoints(v) {
  if (!isNum(v)) return '—';
  const abs = trim(Math.abs(v).toFixed(2));
  if (v > 0) return `+${abs}`;
  if (v < 0) return `${MINUS}${abs}`;
  return '0';
}

/** A price, two decimals (grouped). */
export function fmtPrice(v) {
  if (!isNum(v)) return '—';
  const s = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `${MINUS}${s}` : s;
}

/** A signed price difference, two decimals. */
export function fmtPriceDelta(v) {
  if (!isNum(v)) return '—';
  const s = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (v > 0) return `+${s}`;
  if (v < 0) return `${MINUS}${s}`;
  return '0.00';
}

/** A plain recorded number as stored (a ratio, a percent value, a count). */
export function fmtPlain(v) {
  if (!isNum(v)) return '—';
  const s = trim(Math.abs(v).toFixed(2));
  return v < 0 ? `${MINUS}${s}` : s;
}

/** A whole count. */
export function fmtCount(v) {
  return isNum(v) ? String(Math.round(v)) : '—';
}

/** A ratio as a signed percent, two decimals ("+0.53%"), the true minus sign; the sign is the shown digits'. */
export function fmtPercent(v) {
  if (!isNum(v)) return '—';
  const r = Math.round(v * 10000) / 100;
  const s = `${Math.abs(r).toFixed(2)}%`;
  if (r > 0) return `+${s}`;
  if (r < 0) return `${MINUS}${s}`;
  return s;
}

/** Volume, compact (12.3K / 4.1M). */
export function fmtVolume(v) {
  if (!isNum(v)) return '—';
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${Math.round(v / 1e3)}K`;
  return String(Math.round(v));
}

/** Sign colours belong to recorded SCORES only (BA-41): these paths, when their class is `recorded`. */
const SCORE_PATHS = [
  /^score\.(lastCheck|firstCheck)\.total$/,
  /^score\.lastCheck\.opponent$/,
  /^battle\.final\.(total|opponent)$/,
  /^checks\[\]\.scores\.total$/,
  /^actions\[\]\.lockedPoints$/,
];
export function isRecordedScore(doc, path) {
  if (classAt(doc, path) !== 'recorded') return false;
  const generic = path.map((s) => (typeof s === 'number' ? '[]' : s)).join('.').replace(/\.\[\]/g, '[]');
  return SCORE_PATHS.some((re) => re.test(generic));
}

// ── checks (BA-8, BA-44; Amendment E F3, F5) ────────────────────────────────

/**
 * Every check state's words — the screen's own copy, as a table the copy inventory pins key by key (review
 * A2FV3-7: a new state fails until it is listed and swept). The completed check's four readings come first.
 */
export const CHECK_STATE_WORDS = Object.freeze({
  default_hold: 'no usable model result · the system held by default',
  swap: 'completed · SWAP',
  hold: 'completed · HOLD',
  completed: 'completed · decision not recorded',
  no_trigger: 'no trigger · no check woke',
  budget_skipped: 'check skipped · budget',
  deferred: 'check deferred · budget',
  gameplan_created: 'plan created',
  gameplan_pending: 'plan pending · awaiting approval',
  proposal_pending: 'proposal pending',
  degraded_quotes: 'degraded quotes',
  cpu_passive: 'CPU passive',
  tick_error: 'tick error',
  no_record: 'no record for this check',
  unknown: 'state not recorded',
});
/** The pip family of each state that is not a completed check (its words are CHECK_STATE_WORDS'). */
export const CHECK_STATE_TONES = Object.freeze({
  no_trigger: 'quiet',
  budget_skipped: 'skipped',
  deferred: 'skipped',
  gameplan_created: 'plan',
  gameplan_pending: 'planPending',
  proposal_pending: 'planPending',
  degraded_quotes: 'skipped',
  cpu_passive: 'quiet',
  tick_error: 'skipped',
  no_record: 'gap',
});

/**
 * What a check row's state reads as. `tone` is the pip family: completed rows
 * are solid, plan rows gold, the platform's non-decisions hollow. A completed
 * check whose HOLD was not the model's (decision.holdKind 'default_failure')
 * says so from the check itself; the tape records THAT the system held by
 * default, not which failure caused it, so the screen never names one (F3).
 */
export function checkStateOf(row) {
  const W = CHECK_STATE_WORDS;
  const s = row?.state;
  const final = row?.decision?.final ?? null;
  if (s === 'completed') {
    if (row?.decision?.holdKind === 'default_failure') return { key: 'default_hold', label: W.default_hold, group: 'completed', tone: 'failed' };
    if (final === 'SWAP') return { key: 'swap', label: W.swap, group: 'completed', tone: 'swap' };
    if (final === 'HOLD') return { key: 'hold', label: W.hold, group: 'completed', tone: 'hold' };
    return { key: 'completed', label: W.completed, group: 'completed', tone: 'hold' };
  }
  if (typeof s === 'string' && Object.prototype.hasOwnProperty.call(CHECK_STATE_TONES, s)) return { key: s, label: W[s], group: s, tone: CHECK_STATE_TONES[s] };
  return { key: 'unknown', label: W.unknown, group: 'unknown', tone: 'gap' };
}

/** The six check states the design of record's strip legend names — as rows, read by checkStateOf (its words, its tones). */
const LEGEND_STATE_ROWS = Object.freeze([
  { state: 'gameplan_created' },
  { state: 'gameplan_pending' },
  { state: 'completed', decision: { final: 'HOLD' } },
  { state: 'completed', decision: { final: 'SWAP' } },
  { state: 'completed', decision: { final: 'HOLD', holdKind: 'default_failure' } },
  { state: 'no_trigger' },
]);

/**
 * The check strip's legend: the six states the design of record names, then
 * any other state the day's rows show, each by its own words, in the order
 * first seen — so every pip on the strip has its swatch.
 */
export function checkStateLegend(tape) {
  const out = LEGEND_STATE_ROWS.map(checkStateOf);
  const seen = new Set(out.map((s) => s.key));
  for (const row of Array.isArray(tape?.checks) ? tape.checks : []) {
    const st = checkStateOf(row);
    if (!seen.has(st.key)) { seen.add(st.key); out.push(st); }
  }
  return out;
}

/** Is this row a check the battle actually ran (not a deferral or a missing record)? */
export const ranCheck = (row) => !['deferred', 'no_record'].includes(row?.state);

/**
 * F5 — the day's checks as RUNS: one entry per unbroken stretch of rows that
 * read the same group, with its time span, its rows (by index) and a count.
 * Completed checks form one group whose label lists its HOLD / SWAP / default
 * holds by kind.
 */
export function checkRuns(tape) {
  const rows = Array.isArray(tape?.checks) ? tape.checks : [];
  const runs = [];
  rows.forEach((row, index) => {
    const st = checkStateOf(row);
    const last = runs[runs.length - 1];
    if (last && last.group === st.group) {
      last.indices.push(index);
      last.to = row.at ?? last.to;
      last.kinds[st.key] = (last.kinds[st.key] || 0) + 1;
    } else {
      runs.push({ group: st.group, tone: st.tone, label: st.group === 'completed' ? 'completed' : st.label, from: row.at ?? null, to: row.at ?? null, indices: [index], kinds: { [st.key]: 1 } });
    }
  });
  return runs.map((r) => ({ ...r, count: r.indices.length }));
}

/**
 * BA-7's words for a recorded risk decision — the screen's own copy, as a table the copy inventory pins key by key
 * (B2.1 A8, A2F3-7, after CHECK_STATE_WORDS): the lead, the HOLD line, the none line, an unrecorded action.
 */
export const RISK_WORDS = Object.freeze({
  recorded: 'Risk decision recorded:',
  hold: 'Risk decision recorded: HOLD',
  none: 'No risk decision recorded',
  actionNotRecorded: 'not recorded',
});
/** One recorded risk decision's words: "Risk decision recorded: HOLD", or the action with its reason. */
export const riskText = (action, reason) => (action === 'HOLD' ? RISK_WORDS.hold : `${RISK_WORDS.recorded} ${action}${str(reason) ? ` · ${reason}` : ''}`);

/** BA-7 / BA-44 — a check's recorded risk decisions, one line per symbol; null → "No risk decision recorded". */
export function riskLines(row) {
  const risk = row?.risk;
  if (!isObj(risk) || !Object.keys(risk).length) return null;
  return Object.keys(risk).sort().map((sym) => {
    const v = risk[sym] || {};
    const action = str(v.action) ?? RISK_WORDS.actionNotRecorded;
    return { symbol: sym, text: riskText(action, v.reason), action };
  });
}

/** One summary line for a check's risk record (the strip and the rows). */
export function riskSummary(row) {
  const lines = riskLines(row);
  if (!lines) return RISK_WORDS.none;
  const fired = lines.filter((l) => l.action !== 'HOLD');
  if (!fired.length) return RISK_WORDS.hold;
  return fired.map((l) => `${l.text.replace(`${RISK_WORDS.recorded} `, `${RISK_WORDS.recorded} ${l.symbol} `)}`).join(' · ');
}

// ── actions (BA-6, BA-11, BA-38; Amendment E F1, BA-47) ─────────────────────

/** The anchor every swap card is addressable by (BA-47): `swap-1`, `swap-2`, … for the 0-based place `i` in time order. */
export const swapAnchor = (i) => `swap-${i + 1}`;

/**
 * Each swap's place in time order, 1-based, by tape index — sequence only
 * (addendum R4(a)): the card's "Swap n" and its #swap-n anchor both read this
 * one sequence, so they never disagree (BUILD_RULES §9). The writer stores
 * actions in time order, so it is the tape's order whenever the tape is the
 * writer's; an action with no recorded instant goes after the timed ones.
 */
export function swapOrdinals(tape) {
  const actions = Array.isArray(tape?.actions) ? tape.actions : [];
  const order = actions.map((a, i) => ({ i, ms: toMs(a?.at) }))
    .sort((x, y) => (x.ms === y.ms ? 0 : x.ms === null ? 1 : y.ms === null ? -1 : x.ms - y.ms) || x.i - y.i);
  const out = new Array(actions.length);
  order.forEach((o, k) => { out[o.i] = k + 1; });
  return out;
}

/**
 * Who made the exit (BA-6), from the recorded mechanism — the line that fired
 * by name. `by` is 'agent' for the agent's own decision, 'platform' for a
 * platform rule (risk manager, rotation, guardrail), 'gameplan' for the
 * gameplan meeting (the agent's proposal with the player's approval — not a
 * platform rule), and 'unrecorded' when the tape names no mechanism: never
 * folded into another maker (review A2L1-1).
 */
export const EXIT_MAKER_WORDS = Object.freeze({
  // `entry` (B2.1 A4, FR-04; Amendment J RJ-4): the maker as the BOUGHT name's annotation says it — "by the agent",
  // "by platform rule · {reason}"; the gameplan meeting's and an unrecorded maker's are this build's words, in kind
  agent: Object.freeze({ short: 'agent', swap: 'the agent', exit: 'Exit by the agent · its own decision', entry: 'by the agent' }),
  platform: Object.freeze({ short: 'rule', swap: 'a platform rule', exit: 'Exit by platform rule', noReason: 'rule not recorded', entry: 'by platform rule' }),
  gameplan: Object.freeze({ short: 'meeting', swap: 'the gameplan meeting', exit: 'Exit by the gameplan meeting', entry: 'by the gameplan meeting' }),
  unrecorded: Object.freeze({ short: 'not recorded', swap: 'a maker not recorded', exit: 'Exit maker not recorded', entry: 'maker not recorded' }),
});
/** The recorded mechanisms that are platform rules (each says "Exit by platform rule · <reason>"). */
const PLATFORM_MECHANISMS = Object.freeze(['platform_risk_manager', 'archetype_rotation', 'guardrail']);
export function exitMakerOf(action) {
  const W = EXIT_MAKER_WORDS;
  const m = action?.mechanism;
  const reason = str(action?.exitReason);
  const withReason = (exit) => (reason ? `${exit} · ${reason}` : exit);
  if (m === 'agent_decision') return { by: 'agent', label: W.agent.exit };
  if (PLATFORM_MECHANISMS.includes(m)) return { by: 'platform', label: `${W.platform.exit} · ${reason ?? W.platform.noReason}` };
  if (m === 'gameplan_meeting') return { by: 'gameplan', label: withReason(W.gameplan.exit) };
  return { by: 'unrecorded', label: withReason(W.unrecorded.exit) };
}

/**
 * B2.1 A4 (FR-04; RJ-4) — who made the swap, as the BOUGHT name's annotation says it: never "Exit by …" on the name
 * that came in. The same recorded mechanism as exitMakerOf, the same reason beside a platform rule. Since Amendment K
 * RK-5 the chart's SOLD-name annotation reads the same makers ("Exit · {time} · {maker}"); exitMakerOf's words stay
 * everywhere else (Glance, Study, the symbol's facts). Amendment L RL-4: the gameplan meeting reads "by the gameplan
 * meeting" with no code after it (every real meeting swap records `gameplan_rotation`); a platform rule keeps its
 * reason (RJ-4), and a maker not recorded keeps any it stored.
 */
export function entryMakerOf(action) {
  const W = EXIT_MAKER_WORDS;
  const { by } = exitMakerOf(action);
  if (by === 'platform') return { by, label: `${W.platform.entry} · ${str(action?.exitReason) ?? W.platform.noReason}` };
  const reason = by === 'agent' || by === 'gameplan' ? null : str(action?.exitReason);
  return { by, label: reason ? `${W[by].entry} · ${reason}` : W[by].entry };
}

/** The last point of a path array (the close), as a concrete path into the tape, or null. */
export function lastPointPath(tape, actionIndex, pathKey) {
  const list = valueAt(tape, ['actions', actionIndex, 'replay', pathKey]);
  if (!Array.isArray(list) || !list.length) return null;
  return ['actions', actionIndex, 'replay', pathKey, list.length - 1, 'points'];
}

/**
 * A replay path's RUNS (Astra F1): its samples in order, split wherever a sample has no value or no recorded
 * instant — the only gaps a check-by-check path has (its samples are the checks', never a fixed bar step, so
 * lineRuns' bucket rule does not apply). A run of one is an ISOLATED sample: no valid neighbour on either side.
 * Nothing is joined across a gap.
 */
export function forkRuns(list) {
  const runs = [];
  let run = null;
  for (const p of Array.isArray(list) ? list : []) {
    if (!isNum(p?.points) || toMs(p?.at) === null) { run = null; continue; }
    if (!run) { run = []; runs.push(run); }
    run.push(p);
  }
  return runs;
}

// ── holdings at the day's start and end (BA-45) ─────────────────────────────

/**
 * No stored field carries the held set, so it is DERIVED from recorded values:
 * the start is the risk-verdict keys of the first check that recorded a risk
 * decision (the positions the risk pass evaluated there); each recorded swap
 * after it moves one slot (out → in). The end is that set after every swap; it
 * is checked against the last risk-recording check's keys plus the swaps made
 * at or after that check. When the two disagree, or no check recorded a risk
 * decision, the grid is not drawn and the reason is the coverage line.
 */
/** The held set's coverage notes — the screen's own words, a table the copy inventory pins (B2.1 A8, A2F3-7). */
export const HOLDINGS_NOTES = Object.freeze({
  noRisk: 'no check recorded a risk decision this day, so the held set cannot be derived',
  notReconciled: 'the recorded risk decisions and swaps of this day do not reconcile into a held set, so the grid is not drawn',
  derived: 'derived from recorded values: the first and last checks\' risk decisions and the day\'s swaps',
});

export function deriveHoldings(tape) {
  const checks = Array.isArray(tape?.checks) ? tape.checks : [];
  const actions = Array.isArray(tape?.actions) ? tape.actions : [];
  const withRisk = checks.map((c, i) => ({ c, i })).filter(({ c }) => isObj(c.risk) && Object.keys(c.risk).length);
  if (!withRisk.length) {
    return { status: 'unavailable', note: HOLDINGS_NOTES.noRisk, start: null, end: null, changes: [] };
  }
  const first = withRisk[0];
  const last = withRisk[withRisk.length - 1];
  const startSyms = Object.keys(first.c.risk).sort();
  const firstMs = toMs(first.c.at);
  const lastMs = toMs(last.c.at);
  const slots = startSyms.map((s) => ({ start: s, end: s, change: null }));
  const changes = [];
  let consistent = true;
  actions.forEach((a, index) => {
    const ms = toMs(a.at);
    const out = str(a.symbolOut);
    const inn = str(a.symbolIn);
    if (!out || !inn) { consistent = false; return; }
    // A swap made inside the first risk-recording check, or after it, moves a slot.
    const madeAtOrAfterFirst = (Number.isInteger(a.tickSeq) && Number.isInteger(first.c.tickSeq) && a.tickSeq >= first.c.tickSeq)
      || (ms !== null && firstMs !== null && ms >= firstMs - 60_000);
    const slot = slots.find((x) => x.end === out);
    if (slot && madeAtOrAfterFirst) {
      slot.end = inn;
      slot.change = { index, at: a.at, by: exitMakerOf(a).by, symbolOut: out, symbolIn: inn };
      changes.push(slot.change);
    } else if (!slot && slots.some((x) => x.end === inn)) {
      // Already reflected in the first check's keys (made before it).
    } else {
      consistent = false;
    }
  });
  // The last risk-recording check saw the set before the swaps made in it or after it.
  const madeInOrAfterLast = (a) => (Number.isInteger(a.tickSeq) && Number.isInteger(last.c.tickSeq) && a.tickSeq >= last.c.tickSeq)
    || (toMs(a.at) !== null && lastMs !== null && toMs(a.at) >= lastMs - 60_000);
  const lastKeys = new Set(Object.keys(last.c.risk));
  const expectAtLast = new Set(slots.map((x) => x.end));
  for (const a of actions.filter(madeInOrAfterLast)) {
    if (expectAtLast.has(a.symbolIn)) { expectAtLast.delete(a.symbolIn); expectAtLast.add(a.symbolOut); }
  }
  const agrees = expectAtLast.size === lastKeys.size && [...expectAtLast].every((s) => lastKeys.has(s));
  if (!consistent || !agrees) {
    return { status: 'unavailable', note: HOLDINGS_NOTES.notReconciled, start: null, end: null, changes: [] };
  }
  return {
    status: 'derived',
    note: HOLDINGS_NOTES.derived,
    start: { at: first.c.at, symbols: slots.map((x) => x.start) },
    end: { at: last.c.at, symbols: slots.map((x) => x.end) },
    slots,
    changes,
  };
}

// ── directives (BA-9) ───────────────────────────────────────────────────────

/**
 * The card's state words, from the tape's cardState — the screen's own words only. The directive's recorded text
 * (canonicalText, retainedDirectiveText) is never composed into them: it renders as a quotation of its own path
 * (Amendment E addendum 2, R7; review A2A1-5).
 */
export const DIRECTIVE_CARD_WORDS = Object.freeze({ filed: 'Directive filed', notFiled: 'No new directive filed' });
export function directiveCardOf(d) {
  if (d?.cardState === 'committed') return { filed: true, title: DIRECTIVE_CARD_WORDS.filed };
  return { filed: false, title: DIRECTIVE_CARD_WORDS.notFiled };
}

// ── meetings (Build B spec §3 T2; Amendment J RJ-1) ─────────────────────────

/** The anchor each meeting card is addressable by: `meeting-1`, `meeting-2`, … for its place in the stored section. */
export const meetingAnchor = (i) => `meeting-${i + 1}`;

/**
 * The day's meetings as the screen may show them: 'absent' — the tape has no meetings section at all (no
 * `coverage.meetings`: written before the meetings were taped), said as an absence (R14); 'ready' otherwise.
 */
export const meetingsStateOf = (tape) => (isObj(tape?.coverage?.meetings) ? 'ready' : 'absent');

/**
 * The meeting a run of "plan pending · awaiting approval" checks waits on, matched by INSTANTS, never by the day: the
 * run's first check falls inside the meeting's life — at or after its `createdAt`, at or before its `resolvedAt`, else
 * its stored deadline (`expiresAt`). A meeting whose life the record cannot bound (no created instant, or neither a
 * resolved instant nor a deadline) matches nothing; of several, the latest created. Its index in `tape.meetings`, or
 * null.
 */
export function meetingOfRun(tape, run) {
  if (run?.group !== 'gameplan_pending') return null;
  const from = toMs(run.from);
  if (from === null) return null;
  const meetings = Array.isArray(tape?.meetings) ? tape.meetings : [];
  let best = null;
  meetings.forEach((m, i) => {
    const created = toMs(m?.createdAt);
    const end = toMs(m?.resolvedAt) ?? toMs(m?.expiresAt);
    if (created === null || end === null || from < created || from > end) return;
    if (best === null || created >= toMs(meetings[best].createdAt)) best = i;
  });
  return best;
}

// ── rationale timeline (BA-46) ──────────────────────────────────────────────

/**
 * The day's recorded rationale and the checks that carry no agent text (a
 * default hold, a budget skip, a deferral), in time order. A state entry is
 * the platform's record of the check — never agent words.
 */
export function rationaleTimeline(tape) {
  const rows = [];
  (Array.isArray(tape?.rationale) ? tape.rationale : []).forEach((r, index) => {
    rows.push({ kind: 'rationale', at: r.at, index });
  });
  (Array.isArray(tape?.checks) ? tape.checks : []).forEach((c, index) => {
    const st = checkStateOf(c);
    if (['default_hold', 'budget_skipped', 'deferred'].includes(st.key)) rows.push({ kind: 'state', at: c.at, index, label: st.label });
  });
  return rows.sort((a, b) => (toMs(a.at) ?? 0) - (toMs(b.at) ?? 0) || (a.kind === b.kind ? 0 : a.kind === 'state' ? -1 : 1));
}

// ── plans (BA-10) ───────────────────────────────────────────────────────────

export const PLAN_DIRECTIONS = Object.freeze({ potential_entry: 'Potential entry', potential_exit: 'Potential exit' });

/** Plans grouped by their check (one card per plan time), each with its tape index. */
export function planGroups(tape, symbol = null) {
  const plans = Array.isArray(tape?.plans) ? tape.plans : [];
  const groups = [];
  plans.forEach((p, index) => {
    if (symbol && p.symbol !== symbol) return;
    const last = groups[groups.length - 1];
    if (last && last.at === p.at) last.items.push(index);
    else groups.push({ at: p.at, items: [index] });
  });
  return groups;
}

// ── deep dive (BA-12, BA-13, BA-43) ─────────────────────────────────────────

/** The symbols the deep dive offers: held first (the day's held set), then the day's other series. */
export function deepSymbols(tape, seriesDocs, holdings) {
  const roles = new Map();
  for (const s of seriesDocs || []) if (str(s?.symbol)) roles.set(s.symbol, s.role ?? null);
  const order = [];
  const push = (s) => { if (s && !order.includes(s)) order.push(s); };
  if (holdings?.status === 'derived') { holdings.start.symbols.forEach(push); holdings.end.symbols.forEach(push); }
  for (const a of Array.isArray(tape?.actions) ? tape.actions : []) { push(a.symbolOut); push(a.symbolIn); }
  for (const [s, role] of roles) if (role !== 'market' && role !== 'sector') push(s);
  return order;
}

/**
 * B2.1 A2 (FR-02; RJ-2): the reason the tape records for a symbol it did not chart — the candle pass's W5 list,
 * `coverage.series.notCharted` — when it is one the screen has words for (`known`); null when the tape records none
 * (a day no candle pass wrote, a tape from before W5) or one the screen does not know: the plain sentence then.
 */
export function notChartedReasonOf(tape, symbol, known) {
  const list = tape?.coverage?.series?.notCharted;
  if (!Array.isArray(list)) return null;
  const hit = list.find((e) => isObj(e) && e.symbol === symbol);
  return hit && typeof hit.reason === 'string' && Object.prototype.hasOwnProperty.call(known, hit.reason) ? hit.reason : null;
}

/** BA-43 — the evidence stamps recorded for `symbol`, one per check that carries one, with the check's index. */
export function evidenceMarkers(tape, symbol) {
  const out = [];
  (Array.isArray(tape?.checks) ? tape.checks : []).forEach((c, index) => {
    const e = isObj(c.evidence) ? c.evidence[symbol] : null;
    // BA-43: a marker sits AT evidenceAt — a stamp whose instant was not recorded gets no marker (review A2L1-8).
    if (!isObj(e) || !isNum(e.px) || toMs(c.evidenceAt) === null) return;
    out.push({ index, at: c.evidenceAt, stampedAt: c.evidenceAt });
  });
  return out;
}

/**
 * B2.1 A2 (RJ-2; review SPR-S-4): the checks that hold a recorded price for `symbol` — EVERY one, its stamp instant
 * recorded or not: that rule places a marker on a chart (evidenceMarkers); a row of the no-chart list is labelled by its
 * check's own time. `{ index }` per check, in the tape's order.
 */
export function recordedPricesOf(tape, symbol) {
  const out = [];
  (Array.isArray(tape?.checks) ? tape.checks : []).forEach((c, index) => {
    const e = isObj(c?.evidence) ? c.evidence[symbol] : null;
    if (isObj(e) && isNum(e.px)) out.push({ index });
  });
  return out;
}

/** A symbol's role words — the screen's own copy, a table the copy inventory pins key by key (B2.1 A8, A2F3-7). */
export const ROLE_WORDS = Object.freeze({
  exitedAt: 'exited at',
  enteredAt: 'entered at',
  unrecordedTime: 'an unrecorded time',
  swapBy: 'a swap by',
  held: 'held at the first and the last risk record',
  planned: 'named in a plan',
  series: 'price series',
});

/** The role a symbol had on the day, from the tape's own rows (never guessed). */
export function roleOf(tape, symbol, holdings) {
  const W = ROLE_WORDS;
  const actions = Array.isArray(tape?.actions) ? tape.actions : [];
  const exited = actions.map((a, i) => ({ a, i })).find(({ a }) => a.symbolOut === symbol);
  const entered = actions.map((a, i) => ({ a, i })).find(({ a }) => a.symbolIn === symbol);
  // A recorded swap is stated before "held": a name bought before the first risk record is in the
  // start set too, and must never read as held all along (review A2V1-11).
  if (exited) return { kind: 'exited', text: `${W.exitedAt} ${etClock(exited.a.at) ?? W.unrecordedTime} · ${exitMakerOf(exited.a).label}`, action: exited.i };
  if (entered) {
    // The tape records who made the EXIT of the swap that brought the name in, not who chose it (review A2L1-12).
    const who = exitMakerOf(entered.a);
    const by = `${W.swapBy} ${EXIT_MAKER_WORDS[who.by].swap}${who.by === 'platform' && entered.a.exitReason ? ` · ${entered.a.exitReason}` : ''}`;
    return { kind: 'entered', text: `${W.enteredAt} ${etClock(entered.a.at) ?? W.unrecordedTime} · ${by}`, action: entered.i };
  }
  if (holdings?.status === 'derived' && holdings.start.symbols.includes(symbol) && holdings.end.symbols.includes(symbol)) {
    return { kind: 'held', text: W.held, action: null };
  }
  const planned = (Array.isArray(tape?.plans) ? tape.plans : []).some((p) => p.symbol === symbol);
  if (planned) return { kind: 'planned', text: W.planned, action: null };
  return { kind: 'series', text: W.series, action: null };
}

/**
 * The Deep dive's two computed facts (addendum R4(a)), from ONE series
 * document's own bars — market operands only, so `market`. Each is null when
 * the document cannot carry it whole: no session open or last close for the
 * change; a bar without a volume for the session's volume; and neither when
 * the candle pass lists the symbol as incomplete (the last bar may not be the
 * close, and the sum would undercount).
 */
export function seriesFacts(doc, tape = null) {
  const bars = Array.isArray(doc?.bars) ? doc.bars : [];
  const incomplete = (Array.isArray(tape?.passes?.candles?.symbolsIncomplete) ? tape.passes.candles.symbolsIncomplete : [])
    .some((s) => s === doc?.symbol || s?.symbol === doc?.symbol);
  if (!bars.length || incomplete) return { change: null, volume: null };
  const open = doc?.sessionOpen?.value;
  const close = bars[bars.length - 1]?.c;
  const change = isNum(open) && open > 0 && isNum(close) ? close / open - 1 : null;
  const volume = bars.every((b) => isNum(b?.v)) ? bars.reduce((sum, b) => sum + b.v, 0) : null;
  return { change, volume };
}

/** The series documents' bar width: the candle pass's 10-minute buckets (api/_utils/filmTape/bars.js aggregate10m). */
export const SERIES_STEP_MS = 10 * 60_000;

/**
 * A drawn line's RUNS (review A2A2-3): the points in order, split wherever the next point lies more than one bar step
 * after the last — a 10-minute bucket missing between the first and the last bar — or carries no value or no
 * instant. A line never crosses a gap and nothing is interpolated across one: each run is drawn on its own.
 * Points are `{ t, v }`, `t` in epoch ms.
 */
export function lineRuns(points, stepMs = SERIES_STEP_MS) {
  const runs = [];
  let run = null;
  for (const p of points || []) {
    if (!isNum(p?.v) || !isNum(p?.t)) { run = null; continue; }
    if (!run || p.t - run[run.length - 1].t > stepMs) { run = []; runs.push(run); }
    run.push(p);
  }
  return runs;
}

/**
 * An SVG path through `points`, one subpath per run (lineRuns) — the pen lifts at every gap. A run of one point is
 * a short tick at that point (its own value, nothing interpolated), so a lone bar between two gaps still shows.
 */
export function linePath(points, x, y, stepMs = SERIES_STEP_MS) {
  return lineRuns(points, stepMs).map((run) => (run.length === 1
    ? `M${(x(run[0].t) - 1).toFixed(1)} ${y(run[0].v).toFixed(1)} L${(x(run[0].t) + 1).toFixed(1)} ${y(run[0].v).toFixed(1)}`
    : run.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)} ${y(p.v).toFixed(1)}`).join(' '))).join(' ');
}

/**
 * A series chart's TIME DOMAIN — the one rule the Deep dive's price chart and the desktop "All symbols" sparklines
 * share (review A2A2-4): the tape day's trading session from the calendar the writers use (sessionOf: open to the
 * calendar's close, an early close included — Amendment E addendum 2, F2); only a date the calendar does not know as
 * a session falls back to the bars' own extent, ending at the last bar's end. `startMs` / `endMs` null when neither
 * gives a span. A bar's close sits at its bar's END (`t` + one step) on this domain.
 */
export function seriesDomain(etDate, doc) {
  const bars = Array.isArray(doc?.bars) ? doc.bars : [];
  const session = sessionOf(etDate);
  const lastMs = toMs(bars[bars.length - 1]?.t);
  const startMs = session ? session.openMs : (toMs(doc?.sessionOpen?.at) ?? toMs(bars[0]?.t));
  const endMs = session ? session.closeMs : (lastMs !== null ? lastMs + SERIES_STEP_MS : null);
  return { session, startMs, endMs, lastMs };
}

/** Each bar's close at its bar's end, as a line's points `{ t, v }` on a series domain. */
export const closePoints = (bars) => (Array.isArray(bars) ? bars : []).map((b) => ({ t: toMs(b?.t) === null ? null : toMs(b.t) + SERIES_STEP_MS, v: b?.c }));

/** R4(b) scaffolding: gridline steps of the % move from the session open — at most five lines across the chart's span. */
const PCT_STEPS = [0.001, 0.0025, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2];
export function pctTicks(lo, hi) {
  if (!isNum(lo) || !isNum(hi) || !(hi > lo)) return [];
  const step = PCT_STEPS.find((s) => (hi - lo) / s <= 5) ?? PCT_STEPS[PCT_STEPS.length - 1];
  const out = [];
  for (let k = Math.ceil(lo / step - 1e-9); k * step <= hi + 1e-9; k += 1) out.push(Number((k * step).toFixed(6)));
  return out;
}

/** Indices of the bars that carry the session's high and low (for axis labels at real bar values). */
export function extremeBars(bars) {
  let hi = -1;
  let lo = -1;
  (bars || []).forEach((b, i) => {
    if (isNum(b?.h) && (hi < 0 || b.h > bars[hi].h)) hi = i;
    if (isNum(b?.l) && (lo < 0 || b.l < bars[lo].l)) lo = i;
  });
  return { hi, lo };
}

export { isNum, toMs };
